import { cn } from '@/lib/utils';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Bell, Trash2 } from 'lucide-react';
import { ICON_MAP, COLOR_STYLES } from './notificationMeta';

const timeAgo = (iso) => {
    try {
        return formatDistanceToNowStrict(new Date(iso), { addSuffix: true, locale: fr });
    } catch {
        return '';
    }
};

export const NotificationItem = ({ notification, onOpen, onDelete }) => {
    const IconComponent = ICON_MAP[notification.icon] || Bell;
    const colorStyle = COLOR_STYLES[notification.color] || COLOR_STYLES.neutral;
    const unread = !notification.is_read;

    return (
        <div
            className={cn(
                'group relative flex items-stretch transition-colors duration-200',
                unread ? 'bg-sky-50/80 hover:bg-sky-100/70' : 'bg-white hover:bg-slate-50',
            )}
            data-testid={`notification-${notification.id}`}
            data-read={unread ? 'false' : 'true'}
        >
            {unread && <span className="absolute left-0 top-0 h-full w-[3px] bg-sky-500" aria-hidden="true" />}
            <button
                type="button"
                onClick={() => onOpen(notification)}
                className="flex flex-1 items-start gap-3 px-4 py-3.5 text-left min-w-0"
                data-testid={`notification-open-${notification.id}`}
            >
                <div className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity',
                    colorStyle.bg, unread ? 'opacity-100' : 'opacity-60',
                )}>
                    <IconComponent className={cn('w-4 h-4', colorStyle.icon)} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className={cn('text-sm leading-snug', unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-500')}>
                            {notification.title}
                        </p>
                        {unread ? (
                            <span className="flex items-center gap-1.5 shrink-0 mt-0.5" data-testid={`notification-unread-marker-${notification.id}`}>
                                <span className="text-[10px] font-medium uppercase tracking-wide text-sky-600">Non lue</span>
                                <span className="w-2 h-2 rounded-full bg-sky-500" />
                            </span>
                        ) : (
                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-300 shrink-0 mt-0.5" data-testid={`notification-read-marker-${notification.id}`}>Lue</span>
                        )}
                    </div>
                    <p className={cn('text-sm mt-0.5 line-clamp-2', unread ? 'text-slate-700' : 'text-slate-400')}>
                        {notification.message}
                    </p>
                    <span className={cn('block text-[11px] mt-1.5', unread ? 'text-slate-500' : 'text-slate-400')}>
                        {timeAgo(notification.created_at)}
                    </span>
                </div>
            </button>
            <div className="flex items-center pr-2">
                <button
                    onClick={(e) => onDelete(notification.id, e)}
                    className="p-2 rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                    aria-label="Supprimer la notification"
                    title="Supprimer"
                    data-testid={`notification-delete-${notification.id}`}
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
